import { useEffect } from "react";

import { FormLabel, Grid, Stack } from "@mui/material";
import { CountryNames, selectableCountryRegionData, structuredAddressRequiresRegion } from "@simplyk/common";
import { FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";

import { isRequired } from "@/common/helpers/validators";
import { getError } from "@/common/helpers/get";
import { COUNTRY_NAME_OPTIONS } from "@/common/constants/countryOptions";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { useDefaultCountryRegion } from "@/common/hooks/useDefaultCountryRegion";
import { useGoogleMapsAutocomplete } from "@/common/hooks/useGoogleMapsAutocomplete";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormSearchableSelectField } from "@/forms-design-system/FormSearchableSelectField";
import { FormTextField } from "@/forms-design-system/FormTextField";

const STRUCTURED_ADDRESS_AUTOCOMPLETE_TYPES: google.maps.places.AutocompleteOptions["types"] = ["address"];

type QuestionFieldAddressProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  required: boolean;
  label: string;
  organization: Parameters<typeof useDefaultCountryRegion>[0];
};

export const areStructuredAddressFieldsRequired = (required: boolean, values: unknown[] | undefined): boolean =>
  required || (values?.some((value) => typeof value === "string" && value.trim().length > 0) ?? false);

export const QuestionFieldAddress = <T extends FieldValues>({
  form,
  name,
  required,
  label,
  organization,
}: QuestionFieldAddressProps<T>) => {
  const { t } = useTranslate();
  const { setValue, getValues } = form;
  const addressInputId = `question-field-address-${name.replaceAll(".", "-")}`;
  const addressName = `${name}.answers.0` as Path<T>;
  const cityName = `${name}.answers.1` as Path<T>;
  const postalCodeName = `${name}.answers.2` as Path<T>;
  const countryName = `${name}.answers.3` as Path<T>;
  const regionName = `${name}.answers.4` as Path<T>;
  const values = useWatch({
    control: form.control,
    name: `${name}.answers` as Path<T>,
  }) as unknown[] | undefined;
  const country = useWatch({ control: form.control, name: countryName }) as string | undefined;
  const { currentCountryRegions, regionOptions } = useCountryRegion(country);
  const { defaultCountry, defaultRegion } = useDefaultCountryRegion(organization);
  const fieldsRequired = areStructuredAddressFieldsRequired(required, values);
  const displayRegionSelect = structuredAddressRequiresRegion(country ?? "");
  const countryError = getError(form.formState.errors, countryName);
  const regionError = getError(form.formState.errors, regionName);
  const { addressComponents } = useGoogleMapsAutocomplete({
    addressInputId,
    types: STRUCTURED_ADDRESS_AUTOCOMPLETE_TYPES,
  });

  useEffect(() => {
    if (!required || getValues(countryName)) {
      return;
    }
    setValue(countryName, defaultCountry as never);
    if (defaultRegion) {
      setValue(regionName, defaultRegion as never);
    }
  }, [countryName, defaultCountry, defaultRegion, getValues, setValue, regionName, required]);

  useEffect(() => {
    if (!country) {
      return;
    }
    const region = getValues(regionName);
    if (region && !currentCountryRegions.some((option) => option.shortCode === region)) {
      setValue(regionName, "" as never, { shouldDirty: true });
    }
  }, [country, currentCountryRegions, getValues, setValue, regionName]);

  useEffect(() => {
    if (!addressComponents) {
      return;
    }
    const options = { shouldDirty: true, shouldValidate: true };
    const selectableCountry = selectableCountryRegionData.find(
      (option) => option.countryShortCode === addressComponents.countryCode
    );
    setValue(addressName, (addressComponents.address?.trim() ?? "") as never, options);
    setValue(cityName, (addressComponents.city ?? "") as never, options);
    setValue(postalCodeName, (addressComponents.postalCode ?? "") as never, options);
    setValue(countryName, (selectableCountry?.countryName ?? "") as never, options);
    setValue(regionName, (selectableCountry ? (addressComponents.region ?? "") : "") as never, options);
  }, [addressComponents, addressName, cityName, countryName, setValue, postalCodeName, regionName]);

  const requiredRule = isRequired(fieldsRequired, t("common", "answerRequired"));

  return (
    <Stack gap={1}>
      <FormLabel required={fieldsRequired}>{label}</FormLabel>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormTextField
            control={form.control}
            name={addressName}
            id={addressInputId}
            rules={[requiredRule]}
            label={t("ticketing", "fulfillment.address")}
            data-test="question-field-address-street"
            required={fieldsRequired}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            control={form.control}
            name={cityName}
            rules={[requiredRule]}
            label={t("ticketing", "fulfillment.city")}
            data-test="question-field-address-city"
            required={fieldsRequired}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            control={form.control}
            name={postalCodeName}
            rules={[requiredRule]}
            label={t("ticketing", "fulfillment.postalCode")}
            data-test="question-field-address-postal-code"
            required={fieldsRequired}
          />
        </Grid>
        <Grid item xs={12} sm={displayRegionSelect ? 6 : 12}>
          <FormSearchableSelectField
            control={form.control}
            name={countryName}
            options={COUNTRY_NAME_OPTIONS}
            rules={[requiredRule]}
            label={t("ticketing", "country")}
            data-test="question-field-address-country"
            required={fieldsRequired}
            error={Boolean(countryError)}
            helperText={countryError?.message}
          />
        </Grid>
        {displayRegionSelect && (
          <Grid item xs={12} sm={6}>
            <FormSearchableSelectField
              control={form.control}
              name={regionName}
              options={regionOptions}
              rules={[requiredRule]}
              label={country === CountryNames.Canada ? t("ticketing", "region") : t("ticketing", "state")}
              data-test="question-field-address-region"
              required={fieldsRequired}
              error={Boolean(regionError)}
              helperText={regionError?.message}
            />
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};
